import { createFileRoute } from "@tanstack/react-router";
import installer from "@/assets/installer.webp";
import installer640 from "@/assets/installer-640.webp";
import windowInterior from "@/assets/window-interior.webp";
import windowInterior320 from "@/assets/window-interior-320.webp";
import windowBay from "@/assets/window-bay.webp";
import windowBay320 from "@/assets/window-bay-320.webp";
import windowCasement from "@/assets/window-casement.webp";
import windowCasement320 from "@/assets/window-casement-320.webp";
import windowSlider from "@/assets/window-slider.webp";
import windowSlider320 from "@/assets/window-slider-320.webp";
import { SiteHeader } from "@/components/SiteHeader";
import { QuoteForm } from "@/components/QuoteForm";
import { Reveal, CountUp } from "@/components/Reveal";
import {
  SITE_URL,
  SITE_NAME,
  SITE_PHONE_DISPLAY,
  SITE_PHONE_HREF,
  SITE_EMAIL,
  SOCIAL_IMAGE,
  SERVICE_CITIES,
  NAV_LINKS,
  PAGE_TITLE,
  PAGE_DESCRIPTION,
  HERO_SRC,
  HERO_SRCSET,
  HERO_SIZES,
  businessJsonLd,
  faqJsonLd,
} from "@/lib/site";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESCRIPTION },
      {
        name: "keywords",
        content:
          "window installation Winston-Salem, replacement windows NC, energy efficient windows, vinyl windows, double hung windows, Triad window installer",
      },
      { name: "geo.region", content: "US-NC" },
      { name: "geo.placename", content: "Winston-Salem" },
      { property: "og:title", content: PAGE_TITLE },
      {
        property: "og:description",
        content:
          "Replacement windows that lower bills and boost curb appeal. Free, no-obligation quote from a locally owned team.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/` },
      { property: "og:image", content: SOCIAL_IMAGE },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Window Installation in Winston-Salem, NC" },
      {
        name: "twitter:description",
        content: "Energy-efficient replacement windows installed by a trusted local team.",
      },
      { name: "twitter:image", content: SOCIAL_IMAGE },
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}/` },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(businessJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
    ],
  }),
  component: WindowLanding,
});

const benefits = [
  {
    title: "Lower Energy Bills",
    desc: "ENERGY STAR® rated glass packages cut heat loss in winter and block summer heat — many homeowners see real savings in month one.",
  },
  {
    title: "Quieter, Calmer Home",
    desc: "Modern double-pane construction dampens outside noise so the living room finally feels like a retreat.",
  },
  {
    title: "No More Drafts",
    desc: "Precision installation and modern weather seals eliminate the cold spots and condensation older windows create.",
  },
  {
    title: "Instant Curb Appeal",
    desc: "Fresh, clean window lines transform a home's exterior — and protect resale value when it matters.",
  },
  {
    title: "Easier to Clean",
    desc: "Tilt-in sashes and smooth-glide hardware mean no more ladders, no more painted-shut frames.",
  },
  {
    title: "Backed by Warranty",
    desc: "Lifetime manufacturer's warranty on every window, plus our 1-year craftsmanship warranty for total peace of mind.",
  },
];

const STYLE_SIZES = "(min-width: 1024px) 290px, (min-width: 640px) 45vw, 92vw";

const styles = [
  {
    name: "Double-Hung",
    img: windowInterior320,
    srcSet: `${windowInterior320} 320w, ${windowInterior} 640w`,
    w: 640,
    h: 480,
    desc: "The classic American window. Both sashes tilt in for easy cleaning.",
  },
  {
    name: "Casement",
    img: windowCasement320,
    srcSet: `${windowCasement320} 320w, ${windowCasement} 640w`,
    w: 640,
    h: 640,
    desc: "Crank-open ventilation with a tight, energy-efficient seal.",
  },
  {
    name: "Bay & Bow",
    img: windowBay320,
    srcSet: `${windowBay320} 320w, ${windowBay} 640w`,
    w: 640,
    h: 640,
    desc: "Architectural statement windows that add space, light, and value.",
  },
  {
    name: "Sliding & Patio",
    img: windowSlider320,
    srcSet: `${windowSlider320} 320w, ${windowSlider} 640w`,
    w: 640,
    h: 640,
    desc: "Smooth horizontal glide — ideal for wide openings and patio access.",
  },
];

const steps = [
  {
    n: "01",
    title: "Free In-Home Consultation",
    desc: "We measure, listen, and walk you through options that fit your home and budget — no pressure, no gimmicks.",
  },
  {
    n: "02",
    title: "Transparent Quote",
    desc: "You'll receive your quote in a reasonable time with exact windows, glass package, and timeline explained. What we quote is what you pay.",
  },
  {
    n: "03",
    title: "Professional Installation",
    desc: "Our crew arrives on time, protects your floors and landscaping, installs cleanly, and hauls the old windows away.",
  },
];

const testimonials = [
  {
    quote:
      "I use these guys for all my window jobs. Vast experience and knowledge with all types of windows and door. I'm a general contractor and every client I've had loved working with the company. I'll continue to use them and recommend anyone looking for windows to use this company without hesitation.",
    name: "General Contractor",
    role: "Verified Google Review",
  },
  {
    quote:
      "They did a great job, replacing my large, bathroom window! The price was good and the window is awesome!",
    name: "Happy Homeowner",
    role: "Verified Google Review",
  },
  {
    quote:
      "These guys were great! Professional, knowledgeable, and kind. They did a great job with my windows and were patient with my schedule. There was prompt communication and follow through and they spent time explaining to me what was needed and figuring out a plan that worked with my budget. I would definitely use them again!",
    name: "Satisfied Customer",
    role: "Verified Google Review",
  },
];

const faqs = [
  {
    q: "How long does window installation take?",
    a: "Most installations are completed in a single day. Larger projects are scheduled across 2–3 days and we'll always share the timeline before we start.",
  },
  {
    q: "Will new windows really lower my energy bills?",
    a: "Yes. Modern replacement windows with Low-E glass and proper installation reduce heat loss, prevent drafts, and improve insulation — so your HVAC works less.",
  },
  {
    q: "Do you handle removal and cleanup of old windows?",
    a: "Absolutely. We remove and dispose of your old windows, protect your home throughout, and leave the space cleaner than we found it.",
  },
  {
    q: "What areas do you serve?",
    a: "We're based in Winston-Salem (27107) and serve the entire Triad — Greensboro, High Point, Kernersville, Clemmons, Lewisville, and surrounding communities.",
  },
  {
    q: "Is the quote really free?",
    a: "Yes. Free, in-home, no obligation. We'll measure, recommend, and get you a quote you can think over in a reasonable time.",
  },
];

function WindowLanding() {
  return (
    <div id="top" className="min-h-screen bg-background">
      <SiteHeader />

      <main id="main-content">
        {/* HERO */}
        <section
          className="hero-scene relative isolate overflow-hidden"
          aria-labelledby="hero-heading"
        >
          <img
            src={HERO_SRC}
            srcSet={HERO_SRCSET}
            sizes={HERO_SIZES}
            alt="Beautiful suburban home with newly installed energy-efficient replacement windows"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="sync"
            loading="eager"
          />

          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
            <div className="max-w-2xl text-white">
              <p className="inline-flex items-center gap-2 rounded-full bg-primary/20 px-3 py-1 text-xs font-bold uppercase tracking-widest text-primary-glow ring-1 ring-primary/40">
                Locally Owned • Winston-Salem, NC
              </p>
              <h1
                id="hero-heading"
                className="mt-5 font-display text-4xl leading-[1.05] text-balance sm:text-6xl lg:text-7xl"
              >
                Replacement Windows That Pay <span className="accent-word">You</span> Back.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-white/85">
                Energy-efficient window installation done right — by a local crew homeowners across
                the Triad actually recommend. Lower bills, quieter rooms, real curb appeal.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#quote"
                  className="inline-flex min-h-12 items-center justify-center overflow-hidden rounded-md bg-gradient-primary px-6 py-4 text-base font-bold uppercase tracking-wide text-navy shadow-glow transition hover:translate-y-[-2px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
                >
                  Get My Free Quote
                </a>
                <a
                  href={SITE_PHONE_HREF}
                  className="inline-flex min-h-12 items-center justify-center rounded-md border-2 border-white/70 bg-white/10 px-6 py-4 text-base font-bold uppercase tracking-wide text-white backdrop-blur transition hover:bg-white hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
                >
                  Call {SITE_PHONE_DISPLAY}
                </a>
              </div>
              <ul className="check-list mt-10 flex flex-wrap gap-6 text-sm text-white/85">
                <li className="flex items-center gap-2">Free in-home consult</li>
                <li className="flex items-center gap-2">Lifetime warranty</li>
                <li className="flex items-center gap-2">Same-day installs</li>
                <li className="flex items-center gap-2">Name-brand windows, local prices</li>
              </ul>
            </div>
          </div>
        </section>

        {/* MARQUEE SERVICE AREA */}
        <section
          aria-labelledby="service-area-heading"
          className="overflow-hidden border-y border-primary/30 bg-primary py-4 text-navy"
        >
          <h2 id="service-area-heading" className="sr-only">
            Service area
          </h2>
          <p className="sr-only">Serving {SERVICE_CITIES.join(", ")}.</p>
          <div className="mask-fade-x flex" aria-hidden="true">
            <p className="flex shrink-0 animate-marquee items-center font-display text-xl uppercase tracking-wider whitespace-nowrap">
              {[...SERVICE_CITIES, ...SERVICE_CITIES].map((c, i) => (
                <span key={`${c}-${i}`} className="pr-12">
                  ◆ {c}
                </span>
              ))}
            </p>
          </div>
        </section>

        {/* TRUST BAR */}
        <section
          aria-labelledby="trust-heading"
          className="panel-grid relative border-b border-border bg-navy text-navy-foreground"
        >
          <h2 id="trust-heading" className="sr-only">
            Why homeowners choose us
          </h2>
          <div className="relative mx-auto grid max-w-7xl gap-6 px-4 py-12 sm:grid-cols-2 sm:px-6 md:grid-cols-4">
            {(
              [
                { n: 25, suffix: "+", small: "Years combined experience" },
                { n: 100, suffix: "%", small: "Locally owned & operated" },
                { label: "1 Day", small: "Typical install timeline" },
                { label: "5 ★", small: "Average homeowner rating" },
              ] as const
            ).map((s, i) => (
              <Reveal key={s.small} variant="up" delay={i * 100} className="text-center">
                <p className="font-display text-5xl text-primary-glow">
                  {"n" in s ? <CountUp value={s.n} suffix={s.suffix} /> : s.label}
                </p>
                <p className="mt-1 text-xs uppercase tracking-widest text-white/70">{s.small}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* BENEFITS */}
        <section
          id="benefits"
          aria-labelledby="benefits-heading"
          className="defer-paint glow-orb relative mx-auto max-w-7xl px-4 py-20 sm:px-6"
        >
          <Reveal variant="up" className="mx-auto max-w-2xl text-center">
            <p className="eyebrow text-primary-ink">Why Replace</p>
            <h2 id="benefits-heading" className="mt-3 font-display text-3xl text-navy sm:text-5xl">
              Six reasons homeowners upgrade with us
            </h2>
            <p className="mt-4 text-muted-foreground">
              New windows aren't just a cosmetic fix — they're one of the highest-ROI upgrades you
              can make to a home.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => (
              <Reveal
                key={b.title}
                as="article"
                variant="up"
                delay={i * 80}
                className="benefit-card group relative h-full overflow-hidden rounded-xl border border-border bg-card p-6 transition duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-sharp"
              >
                <p
                  className="relative mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-primary font-display text-xl text-navy transition group-hover:rotate-6 group-hover:scale-110"
                  aria-hidden="true"
                >
                  ✓
                </p>
                <h3 className="font-display text-xl text-navy">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.desc}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* STYLES */}
        <section
          id="styles"
          aria-labelledby="styles-heading"
          className="defer-paint panel-grid-navy relative overflow-hidden bg-cream py-20"
        >
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
            <Reveal variant="up" className="mx-auto max-w-2xl text-center">
              <p className="eyebrow text-primary-ink">Window Styles</p>
              <h2 id="styles-heading" className="mt-3 font-display text-3xl text-navy sm:text-5xl">
                A style for every home
              </h2>
              <p className="mt-4 text-muted-foreground">
                From classic double-hung to modern bay and patio sliders — we install premium vinyl,
                wood, and composite windows from manufacturers we trust.
              </p>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {styles.map((s, i) => (
                <Reveal
                  key={s.name}
                  as="article"
                  variant="up"
                  delay={i * 100}
                  className="style-card group relative h-full overflow-hidden rounded-xl bg-card shadow-sharp transition duration-300 hover:-translate-y-2 hover:shadow-glow"
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={s.img}
                      srcSet={s.srcSet}
                      alt={`${s.name} replacement window installation example`}
                      width={s.w}
                      height={s.h}
                      loading="lazy"
                      decoding="async"
                      sizes={STYLE_SIZES}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                    />
                    <p className="absolute left-3 top-3 z-10 rounded-full bg-primary/95 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-navy">
                      0{i + 1}
                    </p>
                  </div>
                  <div className="relative z-10 bg-card p-5">
                    <h3 className="font-display text-lg text-navy">{s.name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESS + INSTALLER IMAGE */}
        <section
          id="process"
          aria-labelledby="process-heading"
          className="defer-paint mx-auto max-w-7xl px-4 py-20 sm:px-6"
        >
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal variant="left" className="photo-glow relative">
              <img
                src={installer640}
                srcSet={`${installer640} 640w, ${installer} 800w`}
                alt="Dog Day Renovations installer fitting a new energy-efficient window"
                width={800}
                height={800}
                loading="lazy"
                decoding="async"
                sizes="(min-width: 1024px) 592px, 92vw"
                className="rounded-2xl shadow-sharp"
              />
              <p className="absolute -bottom-6 -right-6 hidden rounded-xl bg-gradient-primary p-6 shadow-glow sm:block animate-float-slow">
                <span className="block font-display text-4xl text-navy">
                  <CountUp value={25} suffix="+" />
                </span>
                <span className="block text-xs font-bold uppercase tracking-widest text-navy">
                  Years combined experience
                </span>
              </p>
            </Reveal>
            <Reveal variant="right">
              <p className="eyebrow text-primary-ink">Our Process</p>
              <h2 id="process-heading" className="mt-3 font-display text-3xl text-navy sm:text-5xl">
                Simple. Honest. Stress-free.
              </h2>
              <p className="mt-4 text-muted-foreground">
                No high-pressure sales pitch. No mystery pricing. Just a clear three-step process
                from first call to final clean-up.
              </p>
              <ol className="relative mt-10 space-y-6 border-l-2 border-dashed border-primary/40 pl-2">
                {steps.map((s, i) => (
                  <Reveal key={s.n} as="li" variant="up" delay={i * 120} className="flex gap-5">
                    <div className="relative -ml-[1.4rem] flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-navy font-display text-xl text-primary-glow shadow-sharp">
                      {s.n}
                    </div>
                    <div>
                      <h3 className="font-display text-xl text-navy">{s.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </ol>
            </Reveal>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section
          id="reviews"
          aria-labelledby="reviews-heading"
          className="defer-paint panel-grid blob-field relative overflow-hidden bg-navy py-20 text-navy-foreground"
        >
          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6">
            <Reveal variant="up" className="mx-auto max-w-2xl text-center">
              <p className="eyebrow-invert text-xs font-bold uppercase tracking-widest">Reviews</p>
              <h2 id="reviews-heading" className="mt-3 font-display text-3xl sm:text-5xl">
                Homeowners who trust us
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {testimonials.map((t, i) => (
                <Reveal
                  key={t.name}
                  as="figure"
                  variant="up"
                  delay={i * 120}
                  className="relative h-full rounded-xl border border-white/10 bg-white/5 p-6 backdrop-blur transition duration-300 hover:-translate-y-1 hover:border-primary-glow/60 hover:bg-white/[0.08]"
                >
                  <p
                    aria-hidden="true"
                    className="absolute -top-5 left-5 font-display text-7xl leading-none text-primary-glow/40"
                  >
                    "
                  </p>
                  <p className="relative text-primary-glow">
                    <span className="sr-only">5 out of 5 stars</span>
                    <span aria-hidden="true">★ ★ ★ ★ ★</span>
                  </p>
                  <blockquote className="relative mt-4 text-sm leading-relaxed text-white/90">
                    <p>"{t.quote}"</p>
                  </blockquote>
                  <figcaption className="mt-5 border-t border-white/10 pt-4">
                    <p className="font-bold">{t.name}</p>
                    <p className="text-xs uppercase tracking-widest text-white/60">{t.role}</p>
                  </figcaption>
                </Reveal>
              ))}
            </div>
            <Reveal variant="up" className="mt-10 text-center">
              <a
                href="https://share.google/zJbX2URu9K2ZvG8ns"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-12 items-center justify-center rounded-md border-2 border-primary-glow px-6 py-3 text-base font-bold uppercase tracking-wide text-primary-glow transition hover:bg-primary-glow hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
              >
                Read More Reviews
              </a>
            </Reveal>
          </div>
        </section>

        {/* FAQ */}
        <section
          id="faq"
          aria-labelledby="faq-heading"
          className="defer-paint mx-auto max-w-4xl px-4 py-20 sm:px-6"
        >
          <Reveal variant="up" className="text-center">
            <p className="eyebrow text-primary-ink">FAQ</p>
            <h2 id="faq-heading" className="mt-3 font-display text-3xl text-navy sm:text-5xl">
              Questions, answered
            </h2>
          </Reveal>
          <Reveal
            variant="up"
            delay={100}
            className="mt-10 divide-y divide-border rounded-xl border border-border bg-card"
          >
            {faqs.map((f, i) => (
              <details key={i} className="group p-6 [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex cursor-pointer items-center justify-between gap-4 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <span className="font-display text-base text-navy sm:text-lg">{f.q}</span>
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-primary font-bold text-navy transition group-open:rotate-45"
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </Reveal>
        </section>

        {/* FINAL CTA + FORM */}
        <section id="quote" aria-labelledby="quote-heading" className="bg-cream py-20">
          <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-2">
            <div>
              <p className="eyebrow text-primary-ink">Get A Free Quote</p>
              <h2
                id="quote-heading"
                className="mt-3 font-display text-3xl text-navy sm:text-5xl text-balance"
              >
                Ready for windows that work as hard as you do?
              </h2>
              <p className="mt-4 max-w-md text-muted-foreground">
                Tell us about your project and we'll get back to you within one business day to
                schedule a free in-home consultation. No pressure. No obligation.
              </p>
              <div className="mt-8 space-y-4">
                <a
                  href={SITE_PHONE_HREF}
                  className="flex min-h-12 items-center gap-4 rounded-lg border border-border bg-white p-4 transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-primary text-xl"
                    aria-hidden="true"
                  >
                    📞
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Call
                    </div>
                    <div className="font-display text-lg text-navy">{SITE_PHONE_DISPLAY}</div>
                  </div>
                </a>
                <a
                  href={`mailto:${SITE_EMAIL}`}
                  className="flex min-h-12 items-center gap-4 rounded-lg border border-border bg-white p-4 transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-primary text-xl"
                    aria-hidden="true"
                  >
                    ✉
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Email
                    </div>
                    <div className="font-display text-lg text-navy break-all">{SITE_EMAIL}</div>
                  </div>
                </a>
                <div className="flex items-center gap-4 rounded-lg border border-border bg-white p-4">
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-primary text-xl"
                    aria-hidden="true"
                  >
                    📍
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Serving
                    </div>
                    <div className="font-display text-lg text-navy">
                      Winston-Salem, NC & the Triad
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <QuoteForm />
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="defer-paint bg-navy text-navy-foreground">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/logo-96.webp"
                srcSet="/logo-96.webp 96w, /logo.webp 192w"
                sizes="56px"
                alt=""
                width={56}
                height={69}
                className="h-14 w-14 object-contain"
                loading="lazy"
                decoding="async"
              />
              <div>
                <div className="font-display text-lg">{SITE_NAME}</div>
                <div className="text-xs uppercase tracking-widest text-white/60">
                  Window Installation Specialists
                </div>
              </div>
            </div>
            <p className="mt-4 text-sm text-white/70">
              Locally owned home renovation experts serving Winston-Salem and the NC Triad with
              quality workmanship and honest pricing.
            </p>
          </div>
          <div>
            <div className="font-display text-sm uppercase tracking-widest text-primary-glow">
              Contact
            </div>
            <address className="mt-4 space-y-2 text-sm not-italic text-white/80">
              <div>
                <a href={SITE_PHONE_HREF} className="hover:text-primary-glow">
                  {SITE_PHONE_DISPLAY}
                </a>
              </div>
              <div>
                <a href={`mailto:${SITE_EMAIL}`} className="hover:text-primary-glow break-all">
                  {SITE_EMAIL}
                </a>
              </div>
              <div>Winston-Salem, NC 27107</div>
            </address>
          </div>
          <nav aria-label="Footer">
            <div className="font-display text-sm uppercase tracking-widest text-primary-glow">
              On This Page
            </div>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-sm text-white/80">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="hover:text-primary-glow">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        <div className="border-t border-white/10">
          <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-5 text-xs text-white/60 sm:flex-row sm:px-6">
            <div>
              © {new Date().getFullYear()} {SITE_NAME}. All rights reserved.
            </div>
            <div>Window Installation • Winston-Salem, NC</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
